import { useEffect, useRef } from 'react';

/**
 * Поява елемента при прокрутці — ADR-0008.
 *
 * Замінює GSAP ScrollTrigger там, де потрібне просто «з'явитися один раз».
 * Спостерігач відключається одразу після спрацювання, тому нічого не висить
 * на сторінці після появи.
 *
 * Під prefers-reduced-motion клас is-visible ставиться відразу, без спостерігача.
 *
 * @param {{ threshold?: number, rootMargin?: string }} options
 * @returns {import('react').RefObject<HTMLElement>}
 */
export function useReveal({ threshold = 0.15, rootMargin = '0px 0px -8% 0px' } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced || typeof IntersectionObserver === 'undefined') {
      node.classList.add('is-visible');
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return ref;
}

/**
 * Те саме, але для групи дочірніх елементів: кожен отримує is-visible
 * із каскадною затримкою. Використовується для списків і сіток карток.
 *
 * @param {string} childSelector CSS-селектор дочірніх елементів
 * @param {{ step?: number, threshold?: number }} options затримка між елементами, мс
 * @param {string|number} revealKey значення, зміна якого змушує перечитати дітей
 *
 * Про revealKey. Сітки на сайті наповнюються після відповіді бекенда, тобто в
 * момент першого запуску ефекту вузла ще немає — сторінка показує лоадер. Без
 * цього параметра ефект виходив на `ref.current === null`, більше не
 * повторювався, і поява карток не спрацьовувала взагалі. Передавайте сюди
 * скаляр, що змінюється разом із вмістом: стан запиту або кількість елементів.
 */
export function useRevealGroup(childSelector, { step = 60, threshold = 0.1 } = {}, revealKey = null) {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const children = Array.from(node.querySelectorAll(childSelector));
    if (children.length === 0) return undefined;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced || typeof IntersectionObserver === 'undefined') {
      children.forEach((child) => child.classList.add('is-visible'));
      return undefined;
    }

    children.forEach((child) => child.classList.add('reveal'));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = children.indexOf(entry.target);
          entry.target.style.transitionDelay = `${Math.max(index, 0) * step}ms`;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold }
    );

    children.forEach((child) => observer.observe(child));

    return () => observer.disconnect();
  }, [childSelector, step, threshold, revealKey]);

  return ref;
}
